'use no memo';

import { type CellContext, type ColumnDef, useTable } from '@tanstack/react-table';
import clsx from 'clsx';
import { createContext, use } from 'react';

import { DataTestIds, OrderDirection } from '@repo/frontend-shared';
import { SDSCheckbox } from '@repo/sds/components/SDSCheckbox';
import { useInfiniteScroll } from '@repo/sds/components/SDSTable/hooks/use-infinite-scroll';
import { useTableFilter } from '@repo/sds/components/SDSTable/hooks/use-table-filter';
import { useTableSelection } from '@repo/sds/components/SDSTable/hooks/use-table-selection';
import { useTableSort } from '@repo/sds/components/SDSTable/hooks/use-table-sort';
import { SDSTableVirtualized } from '@repo/sds/components/SDSTable/SDSTableVirtualized';
import { SDSTableBodyRow } from '@repo/sds/components/SDSTable/sds-table-body-row';
import { SDSTableHeaderRow } from '@repo/sds/components/SDSTable/sds-table-header-row';
import { SDSTableLoadingTrigger } from '@repo/sds/components/SDSTable/sds-table-loading-trigger';
import {
  SDS_TABLE_FEATURES,
  type SDSTableFeatures,
  type TableData,
  type TableProps,
} from '@repo/sds/components/SDSTable/types/table.types';
import { buildTableColumns } from '@repo/sds/components/SDSTable/utils/table-columns-utils';
import { resolveTableProps } from '@repo/sds/components/SDSTable/utils/table-props-utils';

import '@repo/sds/components/SDSTable/styles/SDSTable.scss';

interface SelectionCellProps<T> {
  row: T;
  getRowId: (row: T) => string;
}

const TableSelectionContext = createContext<{
  isSelected: (id: string) => boolean;
  toggleRow: (id: string) => void;
} | null>(null);

function SelectionCell<T>({ row, getRowId }: SelectionCellProps<T>) {
  const selection = use(TableSelectionContext);
  if (!selection) throw new Error('Selection cells require their table selection provider.');
  const rowId = getRowId(row);

  return (
    <div className="SDSTable__selection-cell">
      <SDSCheckbox
        isChecked={selection.isSelected(rowId)}
        onChange={() => selection.toggleRow(rowId)}
        ariaLabel={`Select row ${rowId}`}
        mode="active"
        className="SDSTable__checkbox"
        dataTestId={DataTestIds.SDS.SDS_TABLE_ROW_CHECKBOX}
      />
    </div>
  );
}

function createTableConfig(
  isExternalSorting: boolean,
  sorting: ReturnType<typeof useTableSort>['sorting'],
  setSorting: ReturnType<typeof useTableSort>['setSorting'],
  sortField: string | undefined,
  sortDirection: OrderDirection | undefined,
  globalFilter: string,
  setGlobalFilter: (value: string) => void,
) {
  const getSortingState = () => {
    if (!isExternalSorting) {
      return sorting;
    }
    return sortField ? [{ id: sortField, desc: sortDirection === OrderDirection.DESC }] : [];
  };

  return {
    state: {
      sorting: getSortingState(),
      globalFilter,
    },
    onSortingChange: isExternalSorting ? undefined : setSorting,
    onGlobalFilterChange: setGlobalFilter,
    manualSorting: isExternalSorting,
  };
}

function createControlledSelection<T>(
  selectedIds: string[],
  onSelectionChange: (ids: string[]) => void,
  getRowId: (row: T) => string,
) {
  return {
    selectedRows: new Set(selectedIds),
    isSelected: (id: string) => selectedIds.includes(id),
    toggleRow: (id: string) => {
      const newSelection = selectedIds.includes(id)
        ? selectedIds.filter((x: string) => x !== id)
        : [...selectedIds, id];
      onSelectionChange(newSelection);
    },
    selectAll: (rows: T[]) => {
      const allIds = rows.map(getRowId);
      onSelectionChange(allIds);
    },
    clearSelection: () => onSelectionChange([]),
  };
}

export function SDSTable<T extends TableData>(props: TableProps<T>) {
  const {
    columns,
    data,
    sortable,
    filterable,
    virtualized,
    rowHeight,
    onRowClick,
    onLoadMore,
    paginationKey,
    hasMore,
    isLoading,
    className,
    dataTestId,
    selectable,
    selectedIds,
    onSelectionChange,
    getRowId,
    sortField,
    sortDirection,
    onSort,
    renderActions,
    actionsLabel,
    actionsHeaderDataTestId,
    expandedRowId,
    renderExpandedRow,
  } = resolveTableProps(props);
  const { sorting, setSorting } = useTableSort();
  const { globalFilter, setGlobalFilter } = useTableFilter();
  const isExternalSorting = sortField !== undefined && onSort !== undefined;
  const { containerRef, triggerRef } = useInfiniteScroll({
    onLoadMore,
    hasMore,
    isLoading,
  });

  const internalSelection = useTableSelection<T>();
  const isControlled = selectedIds !== undefined && onSelectionChange !== undefined;

  const selection = isControlled
    ? createControlledSelection(selectedIds ?? new Set(), onSelectionChange ?? (() => {}), getRowId)
    : internalSelection;

  const allSelected = selectable && data.length > 0 && data.every((row: T) => selection.isSelected(getRowId(row)));

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      selection.selectAll(data, getRowId);
    } else {
      selection.clearSelection();
    }
  };

  const renderSelectionCell = ({ row }: CellContext<SDSTableFeatures, T, unknown>) => {
    return <SelectionCell row={row.original} getRowId={getRowId} />;
  };

  const tableColumns: ColumnDef<SDSTableFeatures, T, unknown>[] = buildTableColumns(
    columns,
    sortable,
    selectable,
    renderSelectionCell,
    renderActions,
    actionsLabel,
    actionsHeaderDataTestId,
  );

  const tableConfig = createTableConfig(
    isExternalSorting,
    sorting,
    setSorting,
    sortField,
    sortDirection,
    globalFilter,
    setGlobalFilter,
  );

  const table = useTable({
    features: SDS_TABLE_FEATURES,
    data,
    columns: tableColumns,
    ...tableConfig,
  });

  if (virtualized) {
    return (
      <TableSelectionContext value={selection}>
        <SDSTableVirtualized
          table={table}
          data={data}
          rowHeight={rowHeight}
          onRowClick={onRowClick}
          onLoadMore={onLoadMore}
          paginationKey={paginationKey}
          hasMore={hasMore}
          isLoading={isLoading}
          filterable={filterable}
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
          className={className}
          dataTestId={dataTestId}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={onSort}
        />
      </TableSelectionContext>
    );
  }

  return (
    <TableSelectionContext value={selection}>
      <div className={clsx('SDSTable__container', className)} data-testid={dataTestId} ref={containerRef}>
        {filterable && (
          <div className="SDSTable__filter">
            <input
              aria-label="Filter table rows"
              value={globalFilter ?? ''}
              onChange={e => setGlobalFilter(e.target.value)}
              placeholder="Filter..."
              className="SDSTable__filter-input"
            />
          </div>
        )}

        <table className="SDSTable" data-testid={DataTestIds.SDS.SDS_TABLE}>
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <SDSTableHeaderRow
                key={headerGroup.id}
                headerGroup={headerGroup}
                isExternalSorting={isExternalSorting}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={onSort}
                allSelected={allSelected}
                onSelectAll={handleSelectAll}
              />
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map(row => (
              <SDSTableBodyRow
                // TanStack keeps the original column cell definitions for a
                // stable row model. Include the controlled selection bit so a
                // changed checkbox remounts with the current selection reader.
                key={`${row.id}:${selection.isSelected(getRowId(row.original))}`}
                row={row}
                getRowId={getRowId}
                onRowClick={onRowClick}
                expandedRowId={expandedRowId}
                renderExpandedRow={renderExpandedRow}
                columnsCount={tableColumns.length}
              />
            ))}

            {/* Infinite scroll trigger */}
            {hasMore && (
              <SDSTableLoadingTrigger triggerRef={triggerRef} isLoading={isLoading} colSpan={tableColumns.length} />
            )}
          </tbody>
        </table>
      </div>
    </TableSelectionContext>
  );
}
